import type { Course, CoursesContent, Lesson, QuizQuestion } from './courses';
import { CAREER_COURSES } from './courses-careers';

// The courses the game starts with: the six London Life has run since the Goals tab (same ids, so players'
// progress carries on), now with lessons, notes and quizzes, and the career courses. UK facts as of 2026; the admins keep them current.

const q = (question: string, right: string, ...wrong: string[]): QuizQuestion => ({ q: question, right, wrong });
const lesson = (title: string, notes: string[], questions: QuizQuestion[]): Lesson => ({ title, notes, questions });
const course = (value: Omit<Course, 'visible'>): Course => ({ ...value, visible: true });

const foodHygiene = course({
  id: 'food_hygiene',
  label: 'Food Hygiene Level 2',
  emoji: '🧑🏾‍🍳',
  blurb: 'The certificate every UK kitchen and café asks for: the law, temperatures, the 4 Cs and allergens.',
  skill: 'cooking',
  career: 'barista_shift',
  feePence: 3000,
  lessons: [
    lesson(
      'The law and you',
      [
        'The Food Safety Act 1990 is the main food safety law in Great Britain. Anyone selling food must make sure it is safe to eat and is what it says it is.',
        'A new food business must register with its local council at least 28 days before it starts trading. Council inspectors visit and give a food hygiene rating from 0 to 5 under the Food Standards Agency (FSA) scheme.',
        'Food handlers must report illness. After diarrhoea or vomiting, stay away from work until 48 hours after the symptoms have stopped.',
      ],
      [
        q(
          'How long must a food handler stay off work after diarrhoea or vomiting stops?',
          '48 hours',
          '12 hours',
          '24 hours',
          'One week',
        ),
        q('What is the top score in the Food Hygiene Rating Scheme?', '5', '3', '10', '100'),
        q(
          'Who runs the Food Hygiene Rating Scheme with local councils?',
          'The Food Standards Agency',
          'Ofsted',
          'The NHS',
          'Trading Standards only',
        ),
        q(
          'How long before opening must a new food business register with the council?',
          'At least 28 days',
          'At least 7 days',
          'At least 90 days',
          'There is no need to register',
        ),
        q(
          'What is the main food safety law in Great Britain?',
          'The Food Safety Act 1990',
          'The Licensing Act 2003',
          'The Consumer Rights Act 2015',
          'The Equality Act 2010',
        ),
      ],
    ),
    lesson(
      'Temperatures and the 4 Cs',
      [
        'The 4 Cs of food hygiene are cleaning, cooking, chilling and cross-contamination.',
        'Bacteria multiply fastest between 8°C and 63°C: the "danger zone". Keep fridges at 5°C or below, freezers at −18°C or colder, and hot food held for serving at 63°C or above.',
        'Cook food until it is steaming hot all the way through. A common check is a core temperature of 75°C for 30 seconds.',
      ],
      [
        q('What is the "danger zone" for bacteria?', '8°C to 63°C', '0°C to 5°C', '63°C to 100°C', '−18°C to 0°C'),
        q('What temperature should a fridge be?', '5°C or below', '8°C to 12°C', 'Below 15°C', 'Exactly 0°C'),
        q(
          'Hot food kept for serving must be held at…',
          '63°C or above',
          '40°C or above',
          '50°C or above',
          'Any temperature, if it is covered',
        ),
        q('How cold should a freezer be?', '−18°C or colder', '−5°C', '0°C', '4°C'),
        q('The 4 Cs are cleaning, cooking, chilling and…', 'Cross-contamination', 'Customers', 'Cutlery', 'Cash'),
      ],
    ),
    lesson(
      'Allergens and cross-contamination',
      [
        'UK law names 14 major allergens, including peanuts, milk, eggs, gluten, sesame, fish and shellfish. Customers must be told if a dish contains any of them.',
        "Natasha's Law (from October 2021) means food prepacked for direct sale, like a sandwich made and wrapped on site, must show its full ingredients with allergens emphasised.",
        'Store raw meat on the bottom shelf of the fridge so it cannot drip onto ready-to-eat food. Many kitchens colour-code chopping boards: red for raw meat, blue for raw fish, green for salad and fruit.',
      ],
      [
        q('How many major allergens does UK law name?', '14', '6', '9', '20'),
        q(
          "What does Natasha's Law require on food prepacked for direct sale?",
          'Full ingredients with allergens emphasised',
          'A price label only',
          'A use-by date only',
          'A photo of the food',
        ),
        q('Where in the fridge should raw meat go?', 'The bottom shelf', 'The top shelf', 'The door', 'Next to salad'),
        q(
          'In the usual colour code, what is a red chopping board for?',
          'Raw meat',
          'Salad and fruit',
          'Bread',
          'Cooked meat',
        ),
        q('Which of these is one of the 14 major allergens?', 'Sesame', 'Tomato', 'Rice', 'Chicken'),
      ],
    ),
  ],
});

const publicSpeaking = course({
  id: 'public_speaking',
  label: 'Public Speaking',
  emoji: '🎙️',
  blurb: 'Plan a talk people remember, deliver it with confidence, and handle the questions after.',
  skill: 'charisma',
  career: 'cutty_guide',
  feePence: 5000,
  lessons: [
    lesson(
      'Plan it',
      [
        'Start with your audience: who they are, what they know already and what they need from you. Then decide the one message they should leave with.',
        'Give the talk a clear shape: an opening that grabs attention, three main points (people remember things in threes) and a close that sums up and says what to do next.',
        'Signpost as you go ("First…", "My second point…") so listeners always know where they are.',
      ],
      [
        q(
          'What should you think about first when planning a talk?',
          'Your audience',
          'Your slides',
          'Your outfit',
          'The room',
        ),
        q(
          'Why do speakers often use three main points?',
          'People remember things in threes',
          'Three is the legal limit',
          'It makes the talk longer',
          'It avoids questions',
        ),
        q(
          'What is "signposting"?',
          'Telling listeners where you are in the talk',
          'Pointing at the screen',
          'Putting up signs in the room',
          'Ending early',
        ),
        q(
          'A good close should…',
          'Sum up and say what to do next',
          'Introduce a brand new topic',
          'Apologise for the talk',
          'Just stop mid-sentence',
        ),
        q(
          'How many key messages should listeners leave with?',
          'One clear message',
          'As many as possible',
          'None',
          'Ten',
        ),
      ],
    ),
    lesson(
      'Deliver it',
      [
        'Speak a little slower than you think you need to; nerves speed people up. Pausing after a key point gives it time to land.',
        'Look at people, not your notes or the floor: hold eye contact with one person for a sentence, then move on.',
        'Stand steady, keep your hands open and let them move naturally. Breathe from low in your chest so your voice carries without shouting.',
      ],
      [
        q(
          'What does a pause after a key point do?',
          'Gives it time to land',
          'Wastes time',
          'Shows you forgot',
          'Ends the talk',
        ),
        q(
          'Nerves usually make speakers…',
          'Speak too fast',
          'Speak too slowly',
          'Speak too quietly only',
          'Stop blinking',
        ),
        q(
          'Good eye contact means…',
          'Looking at one person for a sentence, then another',
          'Staring at your notes',
          'Looking at the ceiling',
          'Closing your eyes',
        ),
        q('Open hands and natural gestures look…', 'Confident', 'Rude', 'Nervous', 'Unprofessional'),
        q(
          'To make your voice carry without shouting…',
          'Breathe from low in your chest',
          'Speak through your nose',
          'Hold your breath',
          'Whisper into the microphone',
        ),
      ],
    ),
    lesson(
      'Nerves and questions',
      [
        'Nerves are adrenaline getting you ready; nearly every speaker feels them. Slow breathing and practising out loud, timed, are the best cures.',
        'At question time, listen to the whole question, repeat it so the room hears it, then answer briefly.',
        'If you don\'t know the answer, say so and offer to find out. Making something up is worse than "I don\'t know".',
      ],
      [
        q('What causes the nervous feeling before a talk?', 'Adrenaline', 'Hunger', 'Bad slides', 'Cold rooms'),
        q(
          'What is one of the best ways to prepare?',
          'Practise out loud and time it',
          'Read it silently once',
          'Learn it word for word the night before only',
          'Skip practice to stay fresh',
        ),
        q(
          'Why repeat a question before answering?',
          'So the whole room hears it',
          'To waste time',
          'To avoid answering',
          'Because it is polite to copy',
        ),
        q(
          'If you do not know the answer, you should…',
          'Say so and offer to find out',
          'Make something up',
          'Ignore the question',
          'End the talk',
        ),
        q('Answers to questions should usually be…', 'Brief', 'As long as the talk', 'Silent', 'Read from notes'),
      ],
    ),
  ],
});

const firstAid = course({
  id: 'first_aid',
  label: 'First Aid at Work',
  emoji: '⛑️',
  blurb:
    'What every workplace first aider learns: assess the scene, CPR and the defibrillator, bleeding, choking and burns.',
  skill: 'fitness',
  career: 'excel_shift',
  feePence: 6000,
  lessons: [
    lesson(
      'Assess and call for help',
      [
        'Use DR ABC: Danger (make sure you are safe), Response (are they awake?), Airway (tilt the head, lift the chin), Breathing (look, listen and feel for no more than 10 seconds), Circulation.',
        'In the UK call 999 or 112 for an emergency. NHS 111 is for urgent problems that are not emergencies.',
        "The HSE's full First Aid at Work course takes three days; Emergency First Aid at Work takes one. Certificates last three years.",
      ],
      [
        q('What does the D in DR ABC stand for?', 'Danger', 'Doctor', 'Drink', 'Distance'),
        q('Which numbers call the emergency services in the UK?', '999 or 112', '911', '101', '111'),
        q(
          'NHS 111 is for…',
          'Urgent problems that are not emergencies',
          'Fires',
          'Crimes in progress',
          'Cardiac arrests',
        ),
        q(
          'How long should you check for normal breathing?',
          'No more than 10 seconds',
          'One minute',
          'Until help arrives',
          '2 seconds',
        ),
        q('How long does a First Aid at Work certificate last?', 'Three years', 'One year', 'Ten years', 'For life'),
      ],
    ),
    lesson(
      'CPR and the defibrillator',
      [
        'If someone is unresponsive and not breathing normally, call 999 and start CPR: push hard and fast in the centre of the chest, on the lower half of the breastbone.',
        'Give 30 compressions, 5 to 6 cm deep for an adult, at 100 to 120 a minute, then 2 rescue breaths, and repeat. If you cannot give breaths, keep doing hands-only compressions.',
        "A defibrillator (AED) can be used by anyone: switch it on and follow its voice instructions. It won't shock unless it is needed.",
      ],
      [
        q('CPR for an adult: how many compressions before 2 breaths?', '30', '10', '15', '50'),
        q('How fast should compressions be?', '100 to 120 a minute', '40 to 60 a minute', '200 a minute', '1 a second'),
        q('How deep for an adult?', '5 to 6 cm', '1 cm', '10 cm', 'As gently as possible'),
        q(
          'Where do your hands go?',
          'Centre of the chest, lower half of the breastbone',
          'Upper left of the chest',
          'On the stomach',
          'On the neck',
        ),
        q('Who can use a defibrillator (AED)?', 'Anyone', 'Only doctors', 'Only paramedics', 'Only trained nurses'),
      ],
    ),
    lesson(
      'Bleeding, choking and burns',
      [
        'For severe bleeding, press firmly on the wound and keep pressing. If something is stuck in it, leave it there and press around it.',
        'For a choking adult who cannot cough it out, give up to 5 back blows between the shoulder blades, then up to 5 abdominal thrusts. Call 999 if it does not clear.',
        'Cool a burn under cool running water for 20 minutes, then cover it loosely with cling film. Someone unresponsive but breathing normally goes in the recovery position.',
      ],
      [
        q('How long should you cool a burn?', '20 minutes', '1 minute', '5 seconds', '2 hours'),
        q(
          'For severe bleeding you should first…',
          'Press firmly on the wound',
          'Wash it with soap',
          'Give a drink',
          'Let it drain',
        ),
        q(
          'Something is stuck in a wound. You should…',
          'Leave it and press around it',
          'Pull it out quickly',
          'Push it in further',
          'Pour water on it',
        ),
        q('Choking adult: how many back blows first?', 'Up to 5', 'Up to 30', '1', 'Up to 20'),
        q(
          'The recovery position is for someone who is…',
          'Unresponsive but breathing normally',
          'Not breathing',
          'Awake and talking',
          'Choking',
        ),
      ],
    ),
  ],
});

const coding = course({
  id: 'intro_coding',
  label: 'Introduction to Coding',
  emoji: '💻',
  blurb: 'Your first steps in Python: variables, decisions, loops, functions and finding bugs.',
  skill: 'coding',
  career: 'office_shift',
  feePence: 12000,
  lessons: [
    lesson(
      'Programs and variables',
      [
        'A program is a list of instructions the computer follows in order. In Python, print("Hello") shows Hello on the screen.',
        'A variable stores a value under a name: name = "Ada". Text in quotes is a string; 42 is an integer; 3.5 is a float; True and False are booleans.',
      ],
      [
        q(
          'What does a variable do?',
          'Stores a value under a name',
          'Draws a picture',
          'Connects to the internet',
          'Deletes files',
        ),
        q('In Python, "42" (in quotes) is…', 'A string', 'An integer', 'A float', 'A boolean'),
        q('True and False are…', 'Booleans', 'Strings', 'Floats', 'Functions'),
        q('3.5 is…', 'A float', 'An integer', 'A string', 'A boolean'),
        q('What does print("Hi") show?', 'Hi', 'print', '"Hi" with quotes', 'Nothing'),
      ],
    ),
    lesson(
      'Making decisions',
      [
        'if runs code only when a condition is true; elif checks another condition; else runs when none of them were true.',
        'Use == to compare and = to store. Python groups code by indentation, so the spaces at the start of a line matter.',
        'and needs both sides true; or needs one; not flips true and false.',
      ],
      [
        q('Which checks if two values are equal?', '==', '=', '=>', '!'),
        q('When does else run?', 'When none of the conditions were true', 'Always', 'Never', 'Only on errors'),
        q(
          'How does Python group lines of code?',
          'By indentation',
          'By curly brackets',
          'By colours',
          'By line numbers',
        ),
        q('What is not True?', 'False', 'True', 'None', 'An error'),
        q('5 > 3 and 2 > 4 is…', 'False', 'True', '5', 'An error'),
      ],
    ),
    lesson(
      'Loops',
      [
        'A for loop repeats code for each item: for name in ["Ada", "Alan"]: print(name) prints both names.',
        'range(3) gives 0, 1 and 2 (it starts at 0 and stops before 3). A while loop repeats while its condition stays true.',
        'break stops a loop early. A loop whose condition never becomes false runs forever: an infinite loop.',
      ],
      [
        q('What does range(3) give?', '0, 1, 2', '1, 2, 3', '0, 1, 2, 3', '3'),
        q('Which loop repeats while a condition is true?', 'while', 'for each', 'repeat', 'if'),
        q('What does break do?', 'Stops the loop', 'Breaks the computer', 'Skips to the next item', 'Starts it again'),
        q('for x in ["a", "b"]: runs how many times?', '2', '1', '3', 'Forever'),
        q(
          'An infinite loop happens when…',
          'The condition never becomes false',
          'The list is empty',
          'You use print',
          'You use range',
        ),
      ],
    ),
    lesson(
      'Functions',
      [
        'A function is a named, reusable block of code. In Python you write def greet(name): and then the indented lines.',
        'Values passed in are arguments; return sends a result back. def add(a, b): return a + b means add(2, 3) gives 5.',
        "Functions help you follow DRY: Don't Repeat Yourself. Write it once, use it everywhere.",
      ],
      [
        q('Which keyword defines a function in Python?', 'def', 'func', 'function', 'make'),
        q('What does return do?', 'Sends a value back', 'Stops the computer', 'Prints a value', 'Deletes the function'),
        q(
          'What does DRY stand for?',
          "Don't Repeat Yourself",
          'Do Run Yearly',
          'Data Ready Yet',
          'Delete Reused Years',
        ),
        q('def add(a, b): return a + b. What is add(2, 3)?', '5', '23', '6', 'An error'),
        q('Values passed into a function are called…', 'Arguments', 'Loops', 'Strings only', 'Comments'),
      ],
    ),
    lesson(
      'Bugs and next steps',
      [
        'A syntax error means the code breaks the language rules, like a missing bracket. A logic error runs but gives the wrong answer.',
        'Read the error message and the line number first: they usually point straight at the problem.',
        'Developers use git to track changes (version control) and write tests that check code gives the expected result.',
      ],
      [
        q(
          'A syntax error means…',
          "The code breaks the language's rules",
          'The result is wrong',
          'The computer is off',
          'The internet is down',
        ),
        q(
          'A logic error means…',
          'It runs but gives the wrong result',
          'It will not run',
          'It is too slow',
          'It has no comments',
        ),
        q('What is git used for?', 'Tracking changes to code', 'Drawing diagrams', 'Sending email', 'Playing music'),
        q(
          'What should you look at first when you get an error?',
          'The message and the line number',
          'The keyboard',
          'Your emails',
          'Nothing; just try again',
        ),
        q(
          'What does a test do?',
          'Checks the code gives the expected result',
          'Makes code faster',
          'Deletes bugs automatically',
          'Adds colours',
        ),
      ],
    ),
  ],
});

const musicTheory = course({
  id: 'music_theory',
  label: 'Music Theory Grade 5',
  emoji: '🎼',
  blurb: 'Keys, intervals, rhythm and cadences: the grade ABRSM asks for before Grades 6 to 8 practical.',
  skill: 'music',
  career: 'busk',
  feePence: 4500,
  lessons: [
    lesson(
      'Keys and scales',
      [
        'A major scale follows the pattern tone, tone, semitone, tone, tone, tone, semitone.',
        'Sharps are added in the order F, C, G, D, A, E, B. G major has one sharp (F♯); F major has one flat (B♭).',
        'Every major key has a relative minor with the same key signature: C major and A minor have no sharps or flats.',
      ],
      [
        q('Which note is sharp in G major?', 'F♯', 'C♯', 'G♯', 'B♯'),
        q('What is the relative minor of C major?', 'A minor', 'C minor', 'E minor', 'G minor'),
        q('The first sharp in the order of sharps is…', 'F', 'C', 'B', 'G'),
        q("F major's key signature is…", 'One flat, B♭', 'One sharp, F♯', 'No sharps or flats', 'Two flats'),
        q(
          'The major scale pattern is…',
          'Tone, tone, semitone, tone, tone, tone, semitone',
          'All tones',
          'All semitones',
          'Semitone, tone, tone, tone, semitone, tone, tone',
        ),
      ],
    ),
    lesson(
      'Intervals',
      [
        'An interval is the distance between two notes, counted by letter names including both ends: C up to G is a 5th.',
        'C to G is a perfect 5th; C to E is a major 3rd; C to E♭ is a minor 3rd; C to the next C is an octave.',
        'C to F♯, three whole tones, is the tritone: an augmented 4th.',
      ],
      [
        q('C up to G is…', 'A perfect 5th', 'A major 3rd', 'An octave', 'A perfect 4th'),
        q('C up to E is…', 'A major 3rd', 'A minor 3rd', 'A perfect 5th', 'A major 6th'),
        q('C up to E♭ is…', 'A minor 3rd', 'A major 3rd', 'A perfect 4th', 'A minor 2nd'),
        q('C to the next C up is…', 'An octave', 'A 7th', 'A unison', 'A 9th'),
        q('C up to F♯ is…', 'An augmented 4th (the tritone)', 'A perfect 4th', 'A perfect 5th', 'A minor 6th'),
      ],
    ),
    lesson(
      'Time and rhythm',
      [
        'A semibreve lasts 4 crotchet beats, a minim 2, a crotchet 1 and a quaver half a beat.',
        'In simple time each beat splits into two: 3/4 has three crotchet beats in a bar.',
        'In compound time each beat splits into three: 6/8 has two dotted-crotchet beats in a bar.',
      ],
      [
        q('How many crotchets in a semibreve?', '4', '2', '8', '1'),
        q(
          'How many beats in a bar of 3/4?',
          'Three crotchet beats',
          'Three quaver beats',
          'Four crotchet beats',
          'Six',
        ),
        q(
          '6/8 is…',
          'Compound duple: two dotted-crotchet beats',
          'Simple triple',
          'Six crotchet beats',
          'Simple duple',
        ),
        q('A minim lasts…', '2 crotchet beats', '1 beat', '4 beats', 'Half a beat'),
        q('A quaver lasts…', 'Half a crotchet beat', '2 beats', '1 beat', '4 beats'),
      ],
    ),
    lesson(
      'Chords and cadences',
      [
        'The primary chords of a key are I, IV and V. A cadence is the pair of chords ending a phrase.',
        'Perfect cadence: V to I (sounds finished). Plagal: IV to I (the "Amen"). Imperfect: ends on V. Interrupted: V to VI (a surprise).',
        'Italian terms: allegro is fast, adagio is slow, crescendo means getting louder. ABRSM asks for Grade 5 Theory (or an alternative) before Grades 6 to 8 practical.',
      ],
      [
        q('A perfect cadence is…', 'V to I', 'IV to I', 'I to V', 'V to VI'),
        q('The "Amen" cadence is…', 'Plagal, IV to I', 'Perfect, V to I', 'Interrupted', 'Imperfect'),
        q('An interrupted cadence goes…', 'V to VI', 'V to I', 'IV to I', 'I to IV'),
        q('What does crescendo mean?', 'Getting louder', 'Getting softer', 'Getting faster', 'Getting slower'),
        q('Allegro means…', 'Fast', 'Slow', 'Very quiet', 'Sweetly'),
      ],
    ),
  ],
});

const photography = course({
  id: 'photography_basics',
  label: 'Photography Basics',
  emoji: '📷',
  blurb: 'Take control of the camera: the exposure triangle, composition, light, and your rights as a photographer.',
  skill: 'photography',
  career: null,
  feePence: 4000,
  lessons: [
    lesson(
      'The exposure triangle',
      [
        'Exposure is set by three things: aperture, shutter speed and ISO.',
        'A smaller f-number is a wider aperture: f/2.8 lets in more light than f/16 and blurs the background more (a shallower depth of field).',
        'A fast shutter speed like 1/1000 s freezes motion; a slow one blurs it. A higher ISO brightens the picture but adds noise (grain).',
      ],
      [
        q(
          'Which three things set exposure?',
          'Aperture, shutter speed and ISO',
          'Zoom, flash and focus',
          'Lens, battery and card',
          'Colour, contrast and size',
        ),
        q('Which aperture is wider?', 'f/2.8', 'f/16', 'f/22', 'f/11'),
        q('A wider aperture gives…', 'A blurrier background', 'A sharper background', 'More noise', 'Slower focus'),
        q(
          'To freeze a running dog, use…',
          'A fast shutter speed like 1/1000 s',
          'A 2-second shutter',
          'ISO 50',
          'f/22',
        ),
        q('Raising the ISO adds…', 'Noise (grain)', 'Colour', 'Zoom', 'Sharpness'),
      ],
    ),
    lesson(
      'Composition',
      [
        'The rule of thirds: imagine the frame split by two lines each way and place the subject on a line or where they cross.',
        'Leading lines like roads or railings draw the eye into the picture. Fill the frame and keep the horizon level.',
        'Golden hour, just after sunrise and before sunset, gives soft, warm light.',
      ],
      [
        q(
          'The rule of thirds says to place the subject…',
          'On a third line or where the lines cross',
          'Exactly in the centre always',
          'At the very edge',
          'Out of the frame',
        ),
        q(
          'What do leading lines do?',
          'Draw the eye into the picture',
          'Hide the subject',
          'Make it darker',
          'Add noise',
        ),
        q('When is golden hour?', 'Just after sunrise and before sunset', 'Noon', 'Midnight', 'Any time it rains'),
        q('A wonky horizon usually looks…', 'Like a mistake', 'Professional', 'Brighter', 'Sharper'),
        q('Golden hour light is…', 'Soft and warm', 'Harsh and blue', 'Pitch dark', 'Flashing'),
      ],
    ),
    lesson(
      'Light and the law',
      [
        'In the UK there is no general law against taking photos of people or buildings in public places, though owners can set rules on private property.',
        'Under the Copyright, Designs and Patents Act 1988 the photographer owns the copyright, unless they took it as part of their job for an employer.',
        "For adverts and other commercial use of someone's picture, get a signed model release. Shooting in RAW keeps more detail to edit than JPEG.",
      ],
      [
        q(
          'Who usually owns the copyright in a photo in the UK?',
          'The photographer',
          'The person in it',
          'The camera maker',
          'The government',
        ),
        q(
          'Is photography in UK public places generally legal?',
          'Yes, there is no general law against it',
          'No, never',
          'Only with a police permit',
          'Only before 6pm',
        ),
        q(
          'What is a model release for?',
          "Using someone's picture commercially",
          'Buying a camera',
          'Booking a studio',
          'Printing photos',
        ),
        q('Who can set photo rules on private property?', 'The owner', 'Nobody', 'Any visitor', 'The photographer'),
        q('Which format keeps more detail to edit?', 'RAW', 'JPEG', 'GIF', 'A screenshot'),
      ],
    ),
  ],
});

export const DEFAULT_COURSES: CoursesContent = {
  visible: true,
  placeId: 'university',
  classActivityId: 'attend_class',
  classesPerDay: 2,
  questionsPerQuiz: 5,
  passMark: 4,
  missesAllowed: 1,
  retryMinutes: 5,
  lessonXp: 25,
  lessonWishPoints: 1,
  certificateWishPoints: 3,
  certificateXpBoost: 0.25,
  certificateCareerBoost: 0.15,
  // the six skill courses, then the career courses (courses-careers.ts)
  courses: [foodHygiene, publicSpeaking, firstAid, coding, musicTheory, photography, ...CAREER_COURSES],
};
export const coursesOf = (content: { courses?: CoursesContent }) => content.courses ?? DEFAULT_COURSES;
